import type { CertificationPreview } from "@brookbush/types";

import {
	Listbox,
	ListboxButton,
	ListboxOption,
	ListboxOptions,
} from "@headlessui/react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronsUpDown } from "lucide-react";
import { useRouter } from "next/compat/router";
import { useMemo } from "react";

import { useCertification, useSelectedProfessions } from "~/hooks";
import useUserFlags from "~/hooks/useUserFlags";
import { getAcronymForCertification } from "~/utils/helpers";

interface CertificationSelectProps {
	certifications: CertificationPreview[];
}

const COURSES_OPTION = {
	slug: "courses",
	title: "All Courses",
	_id: "all-courses",
	focus: "Continuing Education",
};

const getSelectLabel = (option: CertificationPreview) => {
	if (option.title === "All Courses") {
		return "All Courses";
	}

	return getAcronymForCertification(option);
};

const CertificationSelect: React.FC<CertificationSelectProps> = ({
	certifications,
}) => {
	const router = useRouter();
	const { certification } = useCertification();
	const { selectedProfessions } = useSelectedProfessions();
	const { userFlags } = useUserFlags();

	const isOtherAcknowledged =
		selectedProfessions?.primaryProfession?.slug === "other" &&
		userFlags?.isOtherProfessionAcknowledged === true;

	const options = useMemo(() => {
		return certifications.concat(COURSES_OPTION);
	}, [certifications]);

	const selectedOption: CertificationPreview =
		options.find((cert) => cert.slug === certification?.slug) ?? COURSES_OPTION;

	return (
		<div id="certification-select">
			<Listbox
				value={selectedOption}
				onChange={(cert) => {
					router?.push(
						cert.slug === "courses"
							? `/courses`
							: `/certifications/${cert.slug}`,
					);
				}}
			>
				{({ open }) => (
					<>
						<ListboxButton
							className={clsx(
								"flex w-full items-center justify-center gap-2 laptop:gap-1.5 whitespace-nowrap font-semibold laptop:font-bold laptop:text-2xl text-2xl transition-all duration-200 hover:opacity-50 disabled:animate-pulse dark:hover:opacity-75",
								{
									"text-red-600 hover:text-red-700 dark:text-red-400!":
										selectedOption?.outOfScope && !isOtherAcknowledged,
								},
							)}
						>
							{getSelectLabel(selectedOption)}

							<ChevronsUpDown size={22} className="shrink-0" />
						</ListboxButton>

						<AnimatePresence>
							{open && (
								<ListboxOptions
									static
									as={motion.ul}
									initial={{
										opacity: 0,
										y: -10,
									}}
									exit={{
										opacity: 0,
										y: -10,
										transition: {
											duration: 0.1,
										},
									}}
									animate={{
										opacity: 1,
										y: 0,
										transition: {
											duration: 0.2,
										},
									}}
									className="absolute top-0 left-0 z-10 flex w-full flex-col gap-1.5 overflow-hidden rounded-xl border bg-white p-1.5 shadow-black/10 shadow-xl dark:bg-slate-800"
								>
									{options
										.sort((a, b) =>
											a.slug === selectedOption?.slug
												? -1
												: b.slug === selectedOption?.slug
													? 1
													: 0,
										)

										.map((option) => (
											<ListboxOption
												key={option.slug}
												value={option}
												className={clsx(
													"z-20 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1 font-medium text-lg",
													option.slug === selectedOption?.slug
														? "bg-sky-50 hover:bg-slate-50/50 dark:bg-sky-300/20 dark:text-white dark:hover:bg-slate-700/50"
														: "hover:bg-slate-50 hover:text-black dark:text-white dark:hover:bg-slate-700/50 dark:hover:text-white",
													{
														"text-red-600 hover:text-red-700 dark:text-red-400!":
															option.outOfScope && !isOtherAcknowledged,
													},
												)}
											>
												{option.title}
											</ListboxOption>
										))}
								</ListboxOptions>
							)}
						</AnimatePresence>
					</>
				)}
			</Listbox>
		</div>
	);
};

export default CertificationSelect;
